import { useState, useEffect, useCallback } from "react";
import {
  signInAnonymously,
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  User,
  Auth,
} from "firebase/auth";

export interface AuthState {
  user: User | null;
  isGoogleUser: boolean;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuth = (
  auth: Auth | null,
  isConfigured: boolean
): AuthState => {
  const [user, setUser] = useState<User | null>(null);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isConfigured || !auth) {
      setLoading(false);
      return;
    }

    // Listen for auth state changes first
    // Firebase will restore any persisted session automatically
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User already signed in (persisted session or just signed in)
        setUser(firebaseUser);
        const isGoogle =
          firebaseUser.providerData?.some(
            (p) => p.providerId === "google.com"
          ) ?? false;
        setIsGoogleUser(isGoogle);
        setLoading(false);
      } else {
        // No user - sign in anonymously for browsing
        try {
          // Check for custom token first (Canvas environment)
          if ((window as any).__initial_auth_token) {
            await signInWithCustomToken(
              auth,
              (window as any).__initial_auth_token
            );
          } else {
            await signInAnonymously(auth);
          }
          // onAuthStateChanged will fire again with the new user
        } catch (error) {
          console.error("Auth initialization failed:", error);
          setLoading(false);
        }
      }
    });

    return unsubscribe;
  }, [auth, isConfigured]);

  const signInWithGoogle = useCallback(async () => {
    if (!auth) return;

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // onAuthStateChanged will handle updating state
    } catch (error: any) {
      // User closed popup or other error
      if (error.code !== "auth/popup-closed-by-user") {
        console.error("Google sign-in failed:", error);
        throw error;
      }
    }
  }, [auth]);

  const signOut = useCallback(async () => {
    if (!auth) return;

    try {
      await firebaseSignOut(auth);
      // Sign back in anonymously so they can still browse
      await signInAnonymously(auth);
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  }, [auth]);

  return {
    user,
    isGoogleUser,
    loading,
    signInWithGoogle,
    signOut,
  };
};
